import type { IGame } from '../interfaces/IGame';
import type { ITeam } from '../interfaces/ITeam';
import { SelectionType } from '../enums/selection-type';
import { DateTime } from 'luxon';
import { ordinalSuffix, formatLongDate } from '../date-helpers';

export interface GameBaseOptions {
  home: KnockoutObservable<ITeam>;
  away: KnockoutObservable<ITeam>;
  week: number;
  gameScheduleNum?: number | null;
  gameGuid?: string;
  gameDate?: string;
  gameKey?: string | number;
  primeTimeNum?: number;
  isConferenceGame?: boolean;
}

export abstract class GameBase implements IGame {
  home: KnockoutObservable<ITeam>;
  away: KnockoutObservable<ITeam>;
  status: string;
  value: number;
  primeTimeNum: KnockoutObservable<number>;
  selectionMade: KnockoutObservable<boolean>;
  teamSelected: KnockoutObservable<string>;
  selectionType: KnockoutObservable<SelectionType>;
  TeamThatWon: KnockoutComputed<KnockoutObservable<ITeam>>;
  TeamThatLost: KnockoutComputed<KnockoutObservable<ITeam>>;
  GameClass: KnockoutComputed<string>;
  week: number;
  SortOrder: KnockoutComputed<number>;
  gameDate: KnockoutObservable<DateTime>;
  gameDateShortDisplay: KnockoutComputed<string>;
  gameDateLongDisplay: KnockoutComputed<string>;
  gameNum: KnockoutObservable<number>;
  gameScheduleNum: KnockoutObservable<number>;
  gameGuid: string;
  gameKey: number | string;
  isConferenceGame: boolean;

  constructor(opts: GameBaseOptions) {
    const self = this;

    self.home = opts.home;
    self.away = opts.away;
    self.week = opts.week;
    self.gameGuid = opts.gameGuid;
    self.isConferenceGame = opts.isConferenceGame ?? false;

    self.gameNum = ko.observable(null);
    self.gameScheduleNum = ko.observable(null);
    if (opts.gameScheduleNum != null) {
      self.gameScheduleNum(opts.gameScheduleNum);
    }

    self.selectionMade = ko.observable(false);
    self.teamSelected = ko.observable('NONE');
    self.selectionType = ko.observable(SelectionType.NONE);

    self.primeTimeNum = ko.observable(null);
    if (opts.primeTimeNum) {
      self.primeTimeNum(opts.primeTimeNum);
    }

    self.gameDate = ko.observable(null);
    if (opts.gameDate) {
      self.gameDate(DateTime.fromISO(opts.gameDate));
    }

    if (opts.gameKey != null) {
      self.gameKey = typeof opts.gameKey === 'string' ? parseInt(opts.gameKey, 10) : opts.gameKey;
    }

    self.TeamThatWon = ko.pureComputed(() => self.createTeamThatWon());
    self.TeamThatLost = ko.pureComputed(() => self.createTeamThatLost());
    self.GameClass = ko.pureComputed(() => self.createGameClass());
    self.SortOrder = ko.computed(() => self.createSortOrder());

    self.gameDateShortDisplay = ko.pureComputed(() => {
      if (self.gameDate()) {
        return self.gameDate().toFormat('ccc').slice(0, 2);
      }
      return '';
    });

    self.gameDateLongDisplay = ko.pureComputed(() => {
      if (self.gameDate()) {
        return formatLongDate(self.gameDate()) + ' ET';
      }
      return '';
    });
  }

  protected createTeamThatWon(): KnockoutObservable<ITeam> | null {
    const self = this;
    if (self.selectionMade() && self.selectionType() !== SelectionType.NONE) {
      switch (self.selectionType()) {
        case SelectionType.AWAY_WIN:
        case SelectionType.AWAY_WIN_OVERTIME:
        case SelectionType.AWAY_WIN_SHOOTOUT:
          return self.away;
        case SelectionType.HOME_WIN:
        case SelectionType.HOME_WIN_OVERTIME:
        case SelectionType.HOME_WIN_SHOOTOUT:
          return self.home;
        default:
          return null;
      }
    }
    return null;
  }

  protected createTeamThatLost(): KnockoutObservable<ITeam> | null {
    const self = this;
    if (self.selectionMade() && self.selectionType() !== SelectionType.NONE) {
      switch (self.selectionType()) {
        case SelectionType.AWAY_WIN:
        case SelectionType.AWAY_WIN_OVERTIME:
        case SelectionType.AWAY_WIN_SHOOTOUT:
          return self.home;
        case SelectionType.HOME_WIN:
        case SelectionType.HOME_WIN_OVERTIME:
        case SelectionType.HOME_WIN_SHOOTOUT:
          return self.away;
        default:
          return null;
      }
    }
    return null;
  }

  protected createGameClass(): string {
    const self = this;
    let retStr = '';
    if (self.selectionMade() && self.TeamThatWon()) {
      retStr += 'selection-made ';
      if (
        self.selectionType() !== SelectionType.TIE &&
        self.selectionType() !== SelectionType.NONE
      ) {
        retStr += self.TeamThatWon()().TeamAbbreviation;
      } else if (self.selectionType() === SelectionType.TIE) {
        retStr += 'TIE';
      }
    }
    return retStr;
  }

  protected createSortOrder(): number {
    const self = this;
    if (self.gameDate() && DateTime.isDateTime(self.gameDate())) {
      return self.gameDate().toMillis();
    }
    return 1;
  }

  toString(): string {
    const away = ko.unwrap(this.away);
    const home = ko.unwrap(this.home);
    return `${away.TeamName} @${home.TeamName}`;
  }

  BothTeamNamesInGame(team1: string, team2: string): boolean {
    return (
      (this.away().TeamAbbreviation === team1 || this.away().TeamAbbreviation === team2) &&
      (this.home().TeamAbbreviation === team1 || this.home().TeamAbbreviation === team2)
    );
  }
}
